import {TFunction} from 'i18next';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import autoTable, {Styles} from 'jspdf-autotable';
import moment, {Moment} from 'moment';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {translations} from '../../../../i18n/translations';
import {getTaskStatus} from '../../../../components/shared/badges/task-status';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {User} from '../../../../backend/interfaces/user';
import {CareTaskFeature} from '../../../../backend/enums/care-task-feature';
import {BloodGlucoseData, SleepHealthDataType} from '../../../../backend/interfaces/care-task-data';
import {CareTask} from '../../../../backend/interfaces/care-task';
import {CareTaskNotDoneReason} from '../../../../backend/enums/care-task-not-done-reason';
import {CaregiverTaskCompletion} from '../../../../enums/caregiver-task-completion';
import {pdfHeaderInfo} from '../base/export-pdf-header-info';
import {pdfFooterInfo} from '../base/export-pdf-footer-info';
import {formatPdfDateRangeTitle, pdfAddBodyTitle} from '../base/export-pdf-footer-header-utils';
import {PDF_FONT_MAIN_COLOR} from '../base/export-pdf-font';
import {buildExportFileName} from '../base/export-file-name';
import type {PreparedPdfExportFile, PreparedXlsxExportFile} from '../base/export-data-types';
import {
  PDF_STATUS_COLOR_DONE,
  PDF_STATUS_COLOR_NOT_DONE,
  PDF_STATUS_COLOR_PLANNED,
  PDF_STATUS_COLOR_MISSED,
} from '../base/export-pdf-status-colors';

const STATUS_COLOR: Record<CaregiverTaskCompletion, string> = {
  [CaregiverTaskCompletion.DONE]: PDF_STATUS_COLOR_DONE,
  [CaregiverTaskCompletion.NOT_DONE]: PDF_STATUS_COLOR_NOT_DONE,
  [CaregiverTaskCompletion.PLANNED]: PDF_STATUS_COLOR_PLANNED,
  [CaregiverTaskCompletion.MISSED]: PDF_STATUS_COLOR_MISSED,
};

export function prepareCareActivitiesPDF({
  reportData,
  senior,
  startDate,
  endDate,
  t,
  entity,
}: {
  reportData: any[];
  senior: User;
  startDate: Moment;
  endDate: Moment;
  t: TFunction;
  entity?: EntityInfo;
}): PreparedPdfExportFile {
  const reportTitle = t(translations.seniorSideMenu.careAndActivity);
  const reportDateRange = formatPdfDateRangeTitle({startDate, endDate});
  const pdf = new jsPDF({orientation: 'landscape', compress: true});
  pdf.setProperties({
    title: `${reportTitle}: ${reportDateRange}`,
  });
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);

  const tableStartY = pdfAddBodyTitle(pdf, {
    title: reportTitle,
    subtitle: reportDateRange,
  });

  let table: any[] = [];
  let currentDate: Moment | undefined = undefined;

  for (let entry of reportData) {
    const careTaskDate = moment(entry.startsAt);

    // Add date separator if day changes
    if (!careTaskDate.isSame(currentDate, 'day')) {
      table.push([
        {
          content: careTaskDate.format('DD MMM YY'),
          colSpan: 6,
          styles: {fillColor: '#dcf2ff', fontStyle: 'bold', fontSize: 9},
        },
      ]);
      currentDate = careTaskDate;
    }

    const hour = careTaskDate.format('DD/MM/YYYY HH:mm');
    const activitiesTypeName = entry.activityTypeId.name;
    const handleByUserName =
      entry.markedAsHandledByUserId?.displayName ?? entry.markedAsHandledByUserId?.name ?? '';
    const taskStatus = getTaskStatus({careTask: entry} as CareTaskComplementary);
    const statusLabel = t(translations.care_and_activities.status_types[taskStatus]);

    const cell = (cellContent: string | null, styles?: Partial<Styles>) => ({
      content: cellContent ?? '',
      styles: styles,
    });

    table.push([
      cell(hour),
      cell(activitiesTypeName),
      cell(handleByUserName),
      cell(statusLabel, {textColor: STATUS_COLOR[taskStatus]}),
      cell(createCareTaskRegisterText(t, entry)),
      cell(entry.notes),
    ]);
  }

  // Translations
  const hourStr = t(translations.care_and_activities.date);
  const taskStr = t(translations.care_and_activities.task);
  const handledByStr = t(translations.care_and_activities.responsible);
  const statusStr = t(translations.care_and_activities.status);
  const registerStr = t(translations.care_and_activities.register);
  const notesStr = t(translations.care_and_activities.notes);

  autoTable(pdf, {
    body: table,
    head: [[hourStr, taskStr, handledByStr, statusStr, registerStr, notesStr]],
    startY: tableStartY,
    styles: {fontSize: 9},
    tableWidth: 'auto',
    rowPageBreak: 'avoid',
    columnStyles: {
      // Notes column
      5: {cellWidth: 50},
    },
    willDrawPage: function (data) {
      // ---- Add Header to first page ----
      if (data.pageNumber === 1) {
        pdfHeaderInfo(pdf, senior, entity, t);

        // Reset custom margins for the rest of the pages
        data.settings.margin.top = 7;
        data.settings.margin.bottom = 15;
      }
    },
    didDrawPage: function (data) {
      // ---- Add Footer to all pages ----
      const pageCounter = data.pageNumber.toString();
      pdfFooterInfo(pdf, pageCounter);
    },
  });

  return {
    file: pdf,
    fileName: buildCareActivitiesExportFileName(senior, t),
  };
}

export function prepareCareActivitiesXLSX({
  reportData,
  senior,
  t,
}: {
  reportData: any[];
  senior: User;
  t: TFunction;
}): PreparedXlsxExportFile {
  let rows: string[][] = [];

  for (let entry of reportData) {
    const careTaskDate = moment(entry.startsAt);

    const hour = careTaskDate.format('DD/MM/YYYY HH:mm');
    const activitiesTypeName = entry.activityTypeId.name;
    const handleByUserName =
      entry.markedAsHandledByUserId?.displayName ?? entry.markedAsHandledByUserId?.name ?? '';
    const taskStatus = getTaskStatus({careTask: entry} as CareTaskComplementary);
    const status = t(translations.care_and_activities.status_types[taskStatus]);
    const notes = entry.notes ?? '';

    rows.push([
      hour,
      activitiesTypeName,
      handleByUserName,
      status,
      createCareTaskRegisterText(t, entry),
      notes,
    ]);
  }

  // Translations
  const hourStr = t(translations.care_and_activities.date);
  const taskStr = t(translations.care_and_activities.task);
  const handledByStr = t(translations.care_and_activities.responsible);
  const statusStr = t(translations.care_and_activities.status);
  const registerStr = t(translations.care_and_activities.register);
  const notesStr = t(translations.care_and_activities.notes);

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  // Create a new worksheet
  let worksheet = XLSX.utils.json_to_sheet(rows);
  // Add headers to worksheet
  worksheet = XLSX.utils.sheet_add_aoa(worksheet, [
    [hourStr, taskStr, handledByStr, statusStr, registerStr, notesStr],
  ]);
  // Set columns width
  worksheet['!cols'] = [{wch: 20}, {wch: 30}, {wch: 20}, {wch: 20}, {wch: 50}];
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet);

  return {
    file: workbook,
    fileName: buildCareActivitiesExportFileName(senior, t),
  };
}

function buildCareActivitiesExportFileName(senior: User, t: TFunction): string {
  return buildExportFileName([
    senior.displayName ?? senior.name,
    t(translations.seniorSideMenu.careAndActivity),
  ]);
}

/**
 * @example
 * ```md
 * > 76 kg
 * > 120 - 80 mmHg
 * > 98 %
 * > Not done: Patient refused
 * etc.
 * ```
 */
export function createCareTaskRegisterText(t: TFunction, careTask: CareTask): string | null {
  if (careTask.taskNotDoneReason) {
    return createTextByReasonNotDone(t, careTask.taskNotDoneReason);
  }

  return createTextByCareTaskFeature(t, careTask.data, careTask.feature);
}

function createTextByReasonNotDone(t: TFunction, reason: CareTaskNotDoneReason): string {
  return t(translations.care_taks_not_done_reasons[reason]);
}

export function createTextByCareTaskFeature(
  t: TFunction,
  values: any,
  feature?: CareTaskFeature
): string | null {
  if (!values) return null;

  switch (feature) {
    case CareTaskFeature.BloodOxygenHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BloodPressureHealthMeasurement:
      return values.systolic + ' - ' + values.diastolic + ' mmHg';
    case CareTaskFeature.RespiratoryRateHealthMeasurement:
      return (
        values.breathsPerMinute + ' ' + t(translations.health_measurements.units.breathsPerMinute)
      );
    case CareTaskFeature.BloodGlucoseHealthMeasurement:
      return `${values.value} mg/dL (${t(translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime.options[values.mealTime as BloodGlucoseData['mealTime']])?.toLowerCase()})`;
    case CareTaskFeature.BodyFatPercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BodyMusclePercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BodyWaterPercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.StepsHealthMeasurement:
      return values.value.toString();
    case CareTaskFeature.SleepHealthMeasurement:
      return `${values.value} ${t(translations.time_units.minutes)} (${t(translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[values.type as SleepHealthDataType])?.toLowerCase()})`;
    case CareTaskFeature.BodyHeightHealthMeasurement:
      return values.heightInCentimeters + ' cm';
    case CareTaskFeature.HeartRateHealthMeasurement:
      return values.beatsPerMinute + ' ' + t(translations.health_measurements.units.beatsPerMinute);
    case CareTaskFeature.NatriureticPeptideTestHealthMeasurement:
      return values.bnp + ' - ' + values.nt_proBnp + ' pg/mL';
    case CareTaskFeature.BodyTemperatureHealthMeasurement:
      return values.temperatureInCelsius + ' ºC';
    case CareTaskFeature.BodyWeightHealthMeasurement:
      return values.weightInKilograms + ' Kg';

    default:
      console.log('Unknown data feature:', feature);
      return null;
  }
}
